import React, { createContext, useEffect, useRef, useState } from "react";
import { dynamicHeaderGenerator } from "../utils/dynamicHeaderGenerator";
import { fetchDataFromAPI } from "../api/dataGridApiService";
import { APIResponseType } from "../types";
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridSelectionModel,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import { useQuery, useQueryClient } from "@tanstack/react-query";

// Define the context type
export type DataGridContextType = {
  data: any[];
  setData: React.Dispatch<React.SetStateAction<any[]>>;
  headers: GridColDef[];
  setHeaders: React.Dispatch<React.SetStateAction<GridColDef[]>>;
  backendHeaders: any[];
  setBackendHeaders: React.Dispatch<React.SetStateAction<any[]>>;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  totalPages: number;
  setTotalPages: React.Dispatch<React.SetStateAction<number>>;
  totalDocuments: number;
  setTotalDocuments: React.Dispatch<React.SetStateAction<number>>;
  cache: Record<string, any>;
  setCache: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  error: string;
  setError: React.Dispatch<React.SetStateAction<string>>;
  columnSortModel: GridSortModel;
  setColumnSortModel: React.Dispatch<React.SetStateAction<GridSortModel>>;
  columnVisibilityModel: GridColumnVisibilityModel;
  setColumnVisibilityModel: React.Dispatch<
    React.SetStateAction<GridColumnVisibilityModel>
  >;
  columnFilterModel: GridFilterModel;
  setColumnFilterModel: React.Dispatch<React.SetStateAction<GridFilterModel>>;
  rowSelectionModel: GridSelectionModel;
  setRowSelectionModel: React.Dispatch<
    React.SetStateAction<GridSelectionModel>
  >;
  filterableFields: string[];
  fetchData: (
    endpoint: string,
    params?: Record<string, any>,
    page?: number,
    pageSize?: number,
    filters?: GridFilterModel,
    reset?: boolean,
    sortModel?: GridSortModel
  ) => Promise<void>;
  manualUpdateData: (rowId: string, updates: Record<string, any>) => void;
  forceUpdate: number;
};

// Create the context with default values
export const DataGridContext = createContext<DataGridContextType>({
  data: [],
  setData: () => {},
  headers: [],
  setHeaders: () => {},
  backendHeaders: [],
  setBackendHeaders: () => {},
  currentPage: 1,
  setCurrentPage: () => {},
  totalDocuments: 0,
  setTotalDocuments: () => {},
  cache: {},
  setCache: () => {},
  loading: false,
  setLoading: () => {},
  error: "",
  setError: () => {},
  columnSortModel: [],
  setColumnSortModel: () => {},
  columnVisibilityModel: {},
  setColumnVisibilityModel: () => {},
  columnFilterModel: { items: [] },
  setColumnFilterModel: () => {},
  rowSelectionModel: [],
  setRowSelectionModel: () => {},
  filterableFields: [],
  fetchData: async () => {},
  totalPages: 0,
  setTotalPages: () => {},
  manualUpdateData: () => {},
  forceUpdate: 0,
});

// Define the provider component
const DataGridProvider: React.FC<{ 
  children: React.ReactNode; 
  renderCellMapping: any[]; 
  onCellClick: (params: any, clickedElement: string) => void; 
  sortModel: GridSortModel; 
  visibleColumns: any[]; 
  filterableFields: string[]; 
  externalManualUpdate?: (rowId: string, updates: Record<string, any>) => void;
}> = ({
  children, renderCellMapping, onCellClick, sortModel, visibleColumns, filterableFields, externalManualUpdate,
}) => {

  const isMounted = useRef(true);

  // on unmount, mark it false
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  const [data, setData] = useState<any[]>([]);
  const [headers, setHeaders] = useState<GridColDef[]>([]);
  const [backendHeaders, setBackendHeaders] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cache, setCache] = useState<Record<string, any>>({});
  const [columnSortModel, setColumnSortModel] = useState<GridSortModel>(sortModel);
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});
  const [columnFilterModel, setColumnFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridSelectionModel>([]);
  const [forceUpdate, setForceUpdate] = useState(0);

  const queryClient = useQueryClient();

  // Function to manually update a specific row in the data
  const manualUpdateData = (rowId: string, updates: Record<string, any>) => {
    if (!isMounted.current) return;
    
    console.log(`🔄 Manually updating row with rowId: ${rowId}`, updates);
    
    // If external manualUpdate function is provided, use it
    if (externalManualUpdate) {
      externalManualUpdate(rowId, updates);
      return;
    }
    
    // Update the internal data state
    setData(prevData => {
      const updatedData = [...prevData];
      const rowIndex = updatedData.findIndex(row => row._id === rowId);
      console.log("updatedData", updatedData);

      if (rowIndex >= 0) {
        console.log(`✅ Found row ${rowId} at index ${rowIndex}, updating with:`, updates);
        updatedData[rowIndex] = {
          ...updatedData[rowIndex],
          ...updates,
          last_update: new Date().toISOString()
        };
        console.log(`✅ Successfully updated row ${rowId}`);
      } else {
        console.log(`⚠️ Row ${rowId} not found in current data`);
      }
      
      return updatedData;
    });

    // Also update React Query cache to ensure DataGrid shows the changes
    // Get all queries that might contain this data
    const queries = queryClient.getQueriesData({
      queryKey: ['GET'],
      exact: false
    });

    queries.forEach(([queryKey, queryData]) => {
      if (queryData && typeof queryData === 'object' && 'body' in queryData) {
        const apiResponse = queryData as any;
        if (apiResponse.body && apiResponse.body.data && Array.isArray(apiResponse.body.data)) {
          const dataIndex = apiResponse.body.data.findIndex((row: any) => row._id === rowId);
          if (dataIndex >= 0) {
            console.log(`🔄 Updating React Query cache for query:`, queryKey);
            const updatedQueryData = {
              ...apiResponse,
              body: {
                ...apiResponse.body,
                data: apiResponse.body.data.map((row: any, index: number) => 
                  index === dataIndex 
                    ? { ...row, ...updates, last_update: new Date().toISOString() }
                    : row
                )
              }
            };
            queryClient.setQueryData(queryKey, updatedQueryData);
          }
        }
      }
    });

    // Force a re-render to ensure the DataGrid updates
    setForceUpdate(prev => prev + 1);
  };

  // Function to set column headers dynamically
  const setColumnHeaders = (
    data: any[],
    headers: any[],
    isOpportunity: boolean
  ) => {
    try {
      
      // Find prospects field in backend headers
      const prospectsHeader = headers.find(h => h.db_field === 'prospects' || h.input_file_field === 'prospects');
      
      if (data.length > 0 && renderCellMapping.length) {
        const viewWidth = window.innerWidth - 200;
        const mappedHeaders: GridColDef[] =
          dynamicHeaderGenerator(headers, data, isOpportunity, onCellClick, viewWidth, renderCellMapping, visibleColumns, filterableFields) || [];
        setHeaders(mappedHeaders);
      }
    } catch (error) {
      console.error("Error setting column headers:", error);
    }
  };

  // Function to fetch data from the API using TanStack Query
  const fetchData = async (
    endpoint: string,
    params: Record<string, any> = {},
    page: number = 1,
    pageSize: number = 20,
    filters: GridFilterModel = { items: [] },
    reset = false,
    sortModel: GridSortModel = []
  ) => {
    if (!isMounted.current) return;
    setLoading(true);
    setError("");

    try {

      // Create a unique query key based on all parameters
      const queryKey = [
        'GET',
        endpoint, 
        params, 
        page,
        JSON.stringify(filters),
        JSON.stringify(sortModel)
      ];

      // Use TanStack Query to fetch the data
      const response = await queryClient.ensureQueryData<APIResponseType>({
        queryKey,
        queryFn: async () => {
          return fetchDataFromAPI(endpoint, params, page, pageSize, JSON.stringify(filters), JSON.stringify(sortModel));
        }
      });

      // Update state with the fetched data
      setTotalDocuments(response.body.meta.totalDocuments);
      setCurrentPage(page);
      setTotalPages(response.body.meta.totalPages);
      
      // Store backend headers for filter panel
      setBackendHeaders(response.body.headers);
      
      if (headers.length === 0) {
        setColumnHeaders(
          response.body.data,
          response.body.headers,
          response.body.meta.isOpportunity
        );
      }
      
      if (reset) {
        setData(response.body.data);
      } else {
        setData(old => {
          const combined = [...old, ...response.body.data];
          const uniqueMap = new Map<number, any>();
          combined.forEach(row => {
            uniqueMap.set(row._id, row);
          });
          return Array.from(uniqueMap.values());
        });
      }
    } catch (error) {
      if (!isMounted.current) return;
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError("An unknown error occurred");
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  // Provide the context value
  const contextValue: DataGridContextType = {
    data,
    setData,
    headers,
    setHeaders,
    backendHeaders,
    setBackendHeaders,
    currentPage,
    setCurrentPage,
    totalPages,
    setTotalPages,
    totalDocuments,
    setTotalDocuments,
    cache,
    setCache,
    loading,
    setLoading,
    error,
    setError,
    columnSortModel,
    setColumnSortModel,
    columnVisibilityModel,
    setColumnVisibilityModel,
    columnFilterModel,
    setColumnFilterModel,
    rowSelectionModel,
    setRowSelectionModel,
    filterableFields,
    fetchData,
    manualUpdateData: manualUpdateData,
    forceUpdate: forceUpdate,
  };

  return (
    <DataGridContext.Provider value={contextValue}>
      {children}
    </DataGridContext.Provider>
  );
};

export default DataGridProvider;